import {
  Children,
  Fragment,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'

const EASE = 'cubic-bezier(0.65, 0, 0.35, 1)'
const CHAR_STAGGER = 0.026
const CHAR_DURATION = 1.05

function charSpan(ch: string, key: number, idx: number, visible: boolean, className: string) {
  const delay = idx * CHAR_STAGGER
  return (
    <span
      key={key}
      className={className || undefined}
      style={{
        display: 'inline-block',
        opacity: visible ? 1 : 0,
        filter: visible ? 'blur(0px)' : 'blur(9px)',
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(0.6em) scale(0.92)',
        transition: `opacity ${CHAR_DURATION}s ${EASE} ${delay}s, filter ${CHAR_DURATION}s ${EASE} ${delay}s, transform ${CHAR_DURATION}s ${EASE} ${delay}s`,
        willChange: 'opacity, transform, filter',
      }}
    >
      {ch}
    </span>
  )
}

function splitWords(
  node: ReactNode,
  counter: { current: number },
  visible: boolean,
  inheritedClassName = '',
): ReactNode {
  if (typeof node === 'string') {
    const words = node.split(/( )/)
    return words.map((word, wi) => {
      if (word === '') return null
      if (word === ' ') return ' '
      const chars = Array.from(word)
      return (
        <span key={wi} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
          {chars.map((ch, ci) => charSpan(ch, ci, counter.current++, visible, inheritedClassName))}
        </span>
      )
    })
  }

  if (isValidElement(node)) {
    const el = node as ReactElement<{ children?: ReactNode; className?: string }>
    const combinedClassName = [inheritedClassName, el.props.className].filter(Boolean).join(' ')
    const children = Children.map(el.props.children, (child) =>
      splitWords(child, counter, visible, combinedClassName),
    )
    return <Fragment>{children}</Fragment>
  }

  return node
}

type FluidHeadingProps = {
  children: ReactNode
  className?: string
  as?: 'h1' | 'h2' | 'h3' | 'span'
}

export function FluidHeading({ children, className = '', as = 'h2' }: FluidHeadingProps) {
  const ref = useRef<HTMLElement | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px 0px 0px' },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const counter = { current: 0 }
  const content = Children.map(children, (child) => splitWords(child, counter, visible))
  const Tag = as

  return (
    <Tag ref={ref as never} className={className}>
      {content}
    </Tag>
  )
}
